import * as api from '@yandex-int/k-common/client/api';
import { CLessonInCourse, Course, LESSONS_TYPES, MeViewedHints } from '@yandex-int/k-common/typings';
import { getSpecCourses } from '@yandex-int/k-common/utils/get-spec-courses';
import { showNetworkError } from 'platform-components';
import { getHints } from 'platform-components/src/components/user/selectors';
import { all, call, put, select, takeLatest } from 'redux-saga/effects';

import * as userActions from 'common.components/user/actions';
import { SUBMITTED_FORMS_HINT } from 'common.components/user/user.constants';
import { actions as coursesActions } from 'store/courses/actions';

import { getTeacherCertificateFormId } from '../spec-course.results/selectors';

import { specCourseActions } from './actions';

export function* specCourseSagasWatcher() {
    yield all([
        takeLatest(specCourseActions.getLessons, getLessonsSaga),
        takeLatest(specCourseActions.initCourses, initCoursesSaga),
        takeLatest(specCourseActions.addSpecCourses, addSpecCoursesSaga),
        takeLatest(specCourseActions.makeFormSubmitted, makeFormSubmittedSaga),
    ]);
}

function* loadLessonsByType(courseId: number, type: LESSONS_TYPES) {
    const { clessons } = yield call(api.getLessons, courseId, {
        type,
        pageSize: 50,
        sortKey: null,
    });

    return clessons;
}

function* getLessonsSaga(action: ReturnType<typeof specCourseActions.getLessons>) {
    const { courseId, types, prevClessons = [] } = action.payload;

    try {
        const loadedLessons: CLessonInCourse[][] = yield all(
            types.map((type) => call(loadLessonsByType, courseId, type))
        );

        const newClessons = loadedLessons.reduce((acc: Array<CLessonInCourse>, lessons: Array<CLessonInCourse>) => {
            acc = [...acc, ...lessons];
            return acc;
        }, prevClessons);

        yield put(specCourseActions.setLessons({ clessons: newClessons }));
        yield put(specCourseActions.getLessonsSuccess({ courseId }));
    } catch (error) {
        console.error(error);

        yield put(specCourseActions.getLessonsError({ courseId }));
        yield put(showNetworkError(error));
    }
}

function* addSpecCoursesSaga(action: ReturnType<typeof specCourseActions.addSpecCourses>) {
    try {
        const { teamId, onSuccess } = action.payload;
        // добавляем пустой список курсов - тогда будут выданы только автовыдаваемые курсы
        yield call(api.postAddCourses, { classId: teamId, templateIds: [] });
        yield put(specCourseActions.initCourses({ teamId, onSuccess, shouldNotTryAddSpecCourse: true }));
    } catch (e) {
        console.error(e);
    }
}

function* initCoursesSaga(action: ReturnType<typeof specCourseActions.initCourses>) {
    try {
        const { teamId, onSuccess, shouldNotTryAddSpecCourse } = action.payload;
        const courses: Array<Course> = yield call(api.getCourses, teamId);
        yield put(coursesActions.getCoursesByTeamSuccess({ teamId, courses }));
        const specCourse = getSpecCourses(courses)[0];
        const specCourseId = specCourse?.id;

        if (specCourseId) {
            onSuccess(teamId, specCourseId);
            yield put(specCourseActions.initCoursesSuccess());
        } else if (shouldNotTryAddSpecCourse) {
            // пробуем добавить курсы, но строго однократно
            yield put(specCourseActions.initCoursesSuccess());
        } else {
            // попробовали добавить, ничего не вышло - остаемся где есть, показываем заглушку
            yield put(specCourseActions.addSpecCourses({ teamId, onSuccess }));
        }
    } catch (error) {
        console.error(error);

        yield put(showNetworkError(error));
        yield put(specCourseActions.initCoursesError());
    }
}

function* makeFormSubmittedSaga() {
    try {
        const formId: string = yield select(getTeacherCertificateFormId);
        const hints: MeViewedHints = yield select(getHints);
        const now: unknown = yield call(api.getTime);

        yield put(
            userActions.addViewedHint(SUBMITTED_FORMS_HINT, {
                ...hints[SUBMITTED_FORMS_HINT],
                [formId]: now,
            })
        );
    } catch (error) {
        console.error(error);

        yield put(showNetworkError(error));
    }
}
